/**
 * LightingContext — live-adjustable scene lighting for design work.
 *
 * All 3D pages read intensities from this context instead of using
 * hard-coded values, so the floating <LightingPanel> can tweak the
 * scene in real time. Values persist in localStorage under
 * 'lighting-overrides' so Anna's chosen look survives a reload.
 */

import { createContext, useContext, useEffect, useMemo, useState, useCallback, ReactNode } from 'react'

export interface LightingSettings {
  ambient: number
  key: number
  fill: number
  rim: number
  hemisphere: number
  /** 0–1, drives the strength of the CSS radial halo behind sculptures. */
  backgroundHalo: number
  /** HSL lightness for the page background (0–40). #221F1C ≈ 12. */
  bgLightness: number
}

// Final museum look dialed in via the LightingPanel — single strong
// key light, no fill, soft rim and a much brighter brown backdrop.
export const DEFAULT_LIGHTING: LightingSettings = {
  ambient: 0,
  key: 5,
  fill: 0,
  rim: 0.75,
  hemisphere: 0.3,
  backgroundHalo: 0.06,
  bgLightness: 40,
}

interface LightingContextValue {
  settings: LightingSettings
  setSetting: <K extends keyof LightingSettings>(key: K, value: LightingSettings[K]) => void
  reset: () => void
}

const LightingContext = createContext<LightingContextValue | null>(null)

const STORAGE_KEY = 'lighting-overrides'

export function LightingProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<LightingSettings>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) return { ...DEFAULT_LIGHTING, ...JSON.parse(raw) }
    } catch { /* ignore */ }
    return DEFAULT_LIGHTING
  })

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)) } catch { /* ignore */ }
    // Expose the halo strength as a CSS variable so plain CSS can use it.
    document.documentElement.style.setProperty('--bg-halo-strength', String(settings.backgroundHalo))
  }, [settings])

  const setSetting = useCallback<LightingContextValue['setSetting']>((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }, [])

  const reset = useCallback(() => setSettings(DEFAULT_LIGHTING), [])

  // Memoise the provider value. Without this, every parent re-render
  // creates a fresh `{ settings, setSetting, reset }` object literal,
  // which React's Object.is sees as "changed", which triggers
  // re-renders in every consumer — including R3F's its-fine
  // wrapContext bridge that runs Object.defineProperties on every
  // ported context. Chrome DevTools AI flagged this as a memory growth
  // source.
  const value = useMemo(
    () => ({ settings, setSetting, reset }),
    [settings, setSetting, reset],
  )

  return (
    <LightingContext.Provider value={value}>
      {children}
    </LightingContext.Provider>
  )
}

export function useLighting() {
  const ctx = useContext(LightingContext)
  if (!ctx) throw new Error('useLighting requires <LightingProvider>')
  return ctx
}
